import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLButtonElement} [toggleButton] - The button to pause or play the video.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    this.refs.toggleButton?.addEventListener('click', this.#toggleVideo);
  }

  #toggleVideo = () => {
    const { videoElement, toggleButton } = this.refs;
    if (!toggleButton) return;

    if (videoElement.paused) {
      videoElement.play();
      this.#setPaused(false);
    } else {
      videoElement.pause();
      this.#setPaused(true);
    }
  };

  /**
   * Updates the visual and accessible state for the pause/play control.
   * @param {boolean} paused
   */
  #setPaused(paused) {
    const { toggleButton } = this.refs;
    if (!toggleButton) return;

    this.toggleAttribute('paused', paused);
    toggleButton.setAttribute(
      'aria-label',
      paused ? toggleButton.dataset.playLabel || 'Play video' : toggleButton.dataset.pauseLabel || 'Pause video'
    );
    toggleButton.querySelector('.video-background__toggle-icon--pause')?.toggleAttribute('hidden', paused);
    toggleButton.querySelector('.video-background__toggle-icon--play')?.toggleAttribute('hidden', !paused);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
